import { type EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type EventFormat } from 'components/dialogs/draw/addDraw/utils';
import { type CloudError } from 'repository/CloudError';
import { type BallColour } from 'utils/scalings/pt';

export interface EventCost {
  amountPaid: number;
  amountPayableWithEvents: number;
  personCsId: string;
}

export interface CombinedEventEligibility {
  combineParticipantsEligibility: { participantIdPair: string[]; success?: boolean; error?: CloudError }[];
}

export interface EventEligibility {
  personEventEligibility: {
    eligibilityRuleStatuses?: {
      ruleId: string;
      status: EligibilityStatus;
      strictlyEnforce?: boolean;
    }[];
    eligibilityStatus: EligibilityStatus;
  }[];
}

export interface PersonEventEligibility {
  participantId: string;
  participantName: string;
  eligibilityRuleStatuses?: {
    ruleId: string;
    status: EligibilityStatus;
    strictlyEnforce?: boolean;
  }[];
  eligibilityStatus: EligibilityStatus;
}

export enum SanctionTypeEnum {
  NATIONAL = 'NATIONAL',
  SECTION = 'SECTION',
  DISTRICT = 'DISTRICT',
}

export enum Gender {
  BOYS = 'BOYS',
  COED = 'COED',
  GIRLS = 'GIRLS',
  MIXED = 'MIXED',
  FEMALE = 'FEMALE',
  MALE = 'MALE',
  MEN = 'MEN',
  WOMEN = 'WOMEN',
}

export enum FamilyType {
  FATHER_DAUGHTER = 'FATHER_DAUGHTER',
  FATHER_SON = 'FATHER_SON',
  GRANDFATHER_GRANDSON = 'GRANDFATHER_GRANDSON',
  HUSBAND_WIFE = 'HUSBAND_WIFE',
  MOTHER_DAUGHTER = 'MOTHER_DAUGHTER',
  MOTHER_SON = 'MOTHER_SON',
  SAME_GENDER_FEMALE = 'SAME_GENDER_FEMALE',
  SAME_GENDER_MALE = 'SAME_GENDER_MALE',
}

export enum Category {
  ADULT = 'ADULT',
  JUNIOR = 'JUNIOR',
  WHEELCHAIR = 'WHEELCHAIR',
  WTN_PLAY = 'WTN_PLAY',
}

export enum LeagueTypeEnum {
  CHAMPIONSHIP = 'CHAMPIONSHIP',
  SOCIAL = 'SOCIAL',
}

export enum RatingType {
  NTRP = 'NTRP',
  WTN = 'WTN',
}

export enum RatingCategoryType {
  COMBINED = 'COMBINED',
  INDIVIDUAL = 'INDIVIDUAL',
}

export enum AgeCategoryType {
  COMBINED = 'COMBINED',
  INDIVIDUAL = 'INDIVIDUAL',
}
export interface AgeCategory {
  maximumAge: number | null;
  minimumAge: number | null;
  todsCode: string;
  type: AgeCategoryType;
}

export interface RatingCategory {
  maximumValue: number | null;
  minimumValue: number | null;
  ratingCategoryType: RatingCategoryType;
  ratingType: RatingType;
  value: string | null;
}

export interface EventDivisionData {
  eventType: EventTypeEnum;
  familyType?: FamilyType;
  gender: Gender;
  ageCategory?: AgeCategory;
  ratingCategory?: RatingCategory;
  wheelchairRating?: string;
  tournamentCategory: Category;
}

export enum EligibilityStatus {
  ELIGIBLE = 'ELIGIBLE',
  INELIGIBLE = 'INELIGIBLE',
}

export interface CloseTimeDefaults {
  isEditable: boolean;
  time: number;
  timeZone: string;
}

export interface EventLengthRange {
  maximumDays: number;
}

export interface FamilyDivisionCluster {
  ageCategories: AgeCategory[];
  __typename: string;
}

export interface HeadTax {
  fixedFee: number;
  percentageFee: number;
}

export interface RatingDivisionCluster {
  ageCategories: AgeCategory[];
}

export interface SubmissionWindow {
  timeZone: string;
}

export interface Configuration {
  ageCategories: AgeCategory[];
  allowCustomAgeCategories: boolean;
  allowWildcards: boolean;
  allowsNonConsecutiveDays: boolean;
  ballColours: BallColour[];
  checkInRequired: string;
  closeTimeDefaults: CloseTimeDefaults;
  description: string;
  doublesEventFormats: EventFormat[];
  doublesScoreFormats: string[];
  doublesSelectionProcessConstraints: string[];
  enabled: boolean;
  eventFormats: EventFormat[];
  eventLengthRange: EventLengthRange;
  eventTypes: EventTypeEnum[];
  familyDivisionClusters: FamilyDivisionCluster[];
  genders: Gender[];
  goldSilverBronzeDrawsEnabled: boolean;
  allowSplitDraws: boolean;
  headTax: HeadTax;
  id: number;
  lockedProperties: string[];
  maximumDays: number;
  ratingDivisionClusters: RatingDivisionCluster[];
  sanctionType: SanctionTypeEnum;
  scoreFormats: string[];
  selectionProcessConstraints: string[];
  skillLevel: string;
  submissionWindow: SubmissionWindow;
  teamEventFormats: string[];
  teamSelectionProcessConstraints: string[];
  tournamentFee: number;
}
